import * as React from 'react'
import { History } from 'history'
import { CalendarDays, Tag } from 'lucide-react'
import { TopButtonWrapper } from 'uiComponents/settingsContainer'
import PricingTypeFilter from './pricingTypeFilter'
import { SecondaryTabsRow } from 'uiComponents/navigation/secondaryTabsRow'
import { TableToolbar } from 'uiComponents/table/tableToolbar'
import { KebabMenu, KebabMenuOption } from 'uiComponents/menus/kebabMenu'
import { useHasFeature } from 'features/hocs'
import { usePermission } from 'admin/hocs'
import SearchField from 'uiComponents/search/searchWithButton'
import { PageActionButton, PageAddButton } from 'uiComponents/buttons/pageAddButton'
import { useTranslation } from 'translations'

interface TopButtonProps {
    history: History
    location: { pathname: string }
    accountSlug: string
    origin: string
}

export function ProductsTopButtons(props: TopButtonProps) {
    const { t } = useTranslation()
    const hasProductsBeta = useHasFeature('productsBeta')
    const { hasPermission } = usePermission()
    const isBeta = props.location.pathname.includes('/products/productTable')
    const isFlat = !isBeta && props.location.pathname.indexOf('flat') > -1
    const base = `/account/${props.accountSlug}/products/home`
    const tabs = [
        { name: t('products.viewSelector.categoriesView'), href: `${base}/nested`, current: !isFlat && !isBeta },
        { name: t('products.viewSelector.productsView'), href: `${base}/flat`, current: isFlat },
        ...(hasProductsBeta
            ? [
                  {
                      name: t('products.topButtons.productsBeta'),
                      href: `/account/${props.accountSlug}/products/productTable`,
                      current: isBeta,
                  },
              ]
            : []),
    ].map((tab) => ({ ...tab, onClick: () => props.history.push(tab.href) }))

    const options: KebabMenuOption[] = hasPermission('partner_admin', props.accountSlug)
        ? [
              {
                  label: t('products.topButtons.addCategory'),
                  icon: 'FolderPlus',
                  link: `/account/${props.accountSlug}/products/crud/productList/new?from=${props.origin}`,
              },
              {
                  label: t('products.topButtons.addProduct'),
                  icon: 'TicketPlus',
                  link: `/account/${props.accountSlug}/products/crud/new?from=${props.origin}`,
              },
          ]
        : []

    return (
        <SecondaryTabsRow
            tabs={tabs}
            actions={
                options.length > 0 && (
                    <KebabMenu
                        options={options}
                        listAnchor="right"
                        triggerLabel={t('products.topButtons.moreActions')}
                    />
                )
            }
        />
    )
}

interface ProductsToolbarProps {
    showBulkButtons: boolean
    loading: boolean
    showBulkEditValidity: () => void
    showBulkEditPricing: () => void
}

export function ProductsToolbar(props: ProductsToolbarProps) {
    const { t } = useTranslation()
    const isFlat = location.pathname.indexOf('flat') > -1
    if (!isFlat && !props.showBulkButtons) {
        return null
    }
    return (
        <TableToolbar>
            {isFlat && (
                <div className="header-group">
                    <SearchField placeholder={t('products.topButtons.searchPlaceholder')} disabled={props.loading} />
                    <PricingTypeFilter />
                </div>
            )}
            {props.showBulkButtons && (
                <div className="header-group">
                    <PageActionButton icon={CalendarDays} onClick={props.showBulkEditValidity}>
                        {t('products.topButtons.editValidity')}
                    </PageActionButton>
                    <PageActionButton icon={Tag} onClick={props.showBulkEditPricing}>
                        {t('products.topButtons.editPricing')}
                    </PageActionButton>
                </div>
            )}
        </TableToolbar>
    )
}

interface TopExceptioButtonProps {
    accountSlug: string
    for: 'pricing' | 'validity'
}
export function ExceptionsTopButtons(props: TopExceptioButtonProps) {
    const { t } = useTranslation()
    return (
        <TopButtonWrapper>
            <PageAddButton
                id="newException"
                to={`/account/${props.accountSlug}/products/${props.for}/exceptions/create`}
            >
                {t('products.topButtons.addException')}
            </PageAddButton>
        </TopButtonWrapper>
    )
}

type AddNewTopButtonSection = 'time_slots' | 'inventory' | 'option_groups'

function getAddNewTopButtonId(section: AddNewTopButtonSection) {
    switch (section) {
        case 'time_slots':
            return 'newTimeSlot'
        case 'inventory':
            return 'newInventory'
        case 'option_groups':
            return 'newOptionGroup'
        default:
            return ''
    }
}

function getAddNewTopButtonIHref(section: AddNewTopButtonSection) {
    switch (section) {
        case 'time_slots':
            return `time_slots/edit/new${location.search}`
        case 'inventory':
            return 'inventory/item/new'
        case 'option_groups':
            return 'option_groups/group_details/new'
        default:
            return ''
    }
}

interface AddNewTopButtonProps {
    accountSlug: string
    for: AddNewTopButtonSection
}
export function AddNewTopButton(props: AddNewTopButtonProps) {
    const { t } = useTranslation()
    return (
        <TopButtonWrapper>
            <div data-userpilot={props.for === 'time_slots' ? 'timeslots-list-add-new-button' : ''}>
                <PageAddButton
                    id={getAddNewTopButtonId(props.for)}
                    to={`/account/${props.accountSlug}/products/${getAddNewTopButtonIHref(props.for)}`}
                >
                    {t('products.topButtons.addNew')}
                </PageAddButton>
            </div>
        </TopButtonWrapper>
    )
}
